// Design tokens for the ProjectBank scene (folder tree, item cards and rows,
// list header, filter panel, status bar, tabs, marquee and drop target).
//
// A factory whose every argument is required, for the reason spelled out at the
// top of chatTokens.js: darkTheme and lightTheme are both built as
// merge({}, classicThemeVariables, {...}), so a token defined only in classic is
// silently inherited by the other two. An argument list turns "which themes have
// to supply this" into a call-site error instead of a colour nobody chose.
//
// ## Why the bank needs its own surfaces
//
// The scene used to paint both panes with a hard-coded white background inside
// an otherwise dark blue app, and each tile borrowed palette.primary.* for its
// hover and selection. Those literals looked the same in every theme, so the
// dark theme got two white panes and the light theme got a white pane on a
// white page with no edge between them. These tokens replace every literal:
// each theme names a pane surface, a raised surface for the tree and the list
// header, and a card surface that sits one step above the pane, and every
// accent is then chosen against those surfaces rather than against whatever
// the app happens to be.
//
// ## Paired arguments
//
// Text sits on the card, the overlay disc sits on a photo, the chip sits on the
// card, and the accent fills the selected-count badge and the list header's
// active column. Each of those backgrounds is only correct together with the
// foreground that has to clear 4.5:1 against it, which is why card/cardText,
// overlay/overlayText, chip/chipText and accent/accentText are argument pairs
// and end up in the same token object. The marquee and the drop target reuse
// `accent` for their border with a translucent fill of the same hue, so that a
// selection rectangle and a "drop here" frame read as the same colour family as
// the selected card.

/** ProjectBank tokens for theme.component.*. Every argument is required. */
export const projectBankComponentTokens = ({
  surface,
  surfaceRaised,
  border,
  card,
  cardHover,
  cardSelected,
  cardBorder,
  cardText,
  cardMuted,
  media,
  overlay,
  overlayText,
  chip,
  chipText,
  accent,
  accentText,
  selectionFill,
  dropFill,
}) => ({
  projectBankSurface: { backgroundColor: surface },
  projectBankSurfaceRaised: { backgroundColor: surfaceRaised },
  projectBankBorder: { borderColor: border },
  projectBankCard: {
    backgroundColor: card,
    borderColor: cardBorder,
    color: cardText,
  },
  projectBankCardHover: { backgroundColor: cardHover },
  // The selected card keeps its own fill and borrows the accent for its border,
  // so a selected card and the marquee that selected it share one edge colour.
  projectBankCardSelected: {
    backgroundColor: cardSelected,
    borderColor: accent,
  },
  projectBankMuted: { color: cardMuted },
  // The thumbnail well behind an image, a format icon or a loading skeleton.
  projectBankMedia: { backgroundColor: media },
  // The checkbox disc and kebab painted on top of a thumbnail; they are always
  // visible (never revealed on hover), so the disc has to read on any photo.
  projectBankOverlay: { backgroundColor: overlay, color: overlayText },
  projectBankChip: { backgroundColor: chip, color: chipText },
  projectBankAccent: { backgroundColor: accent, color: accentText },
  projectBankSelectionBox: {
    borderColor: accent,
    backgroundColor: selectionFill,
  },
  projectBankDropTarget: { borderColor: accent, backgroundColor: dropFill },
  projectBankTabs: {
    backgroundColor: surfaceRaised,
    color: cardMuted,
    indicatorColor: accent,
  },
});
